export const formatNumber = (num: number) => {
  let formatter = Intl.NumberFormat("en", { notation: "compact" });
  return formatter.format(num);
};

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Format a date string or Date object in a specific timezone
 * @param dateString - ISO date string or Date object (typically in UTC from API)
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich", "America/New_York")
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string in the specified timezone
 */
export function formatDateInTimezone(
  dateString: string | Date,
  timezone: string = "UTC",
  options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  },
): string {
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return date.toLocaleDateString("en-US", {
    ...options,
    timeZone: timezone,
  });
}

/**
 * Format a time string or Date object in a specific timezone
 * @param dateString - ISO date string or Date object (typically in UTC from API)
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich", "America/New_York")
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted time string in the specified timezone
 */
export function formatTimeInTimezone(
  dateString: string | Date,
  timezone: string = "UTC",
  options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
): string {
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return date.toLocaleTimeString("en-US", {
    ...options,
    timeZone: timezone,
  });
}

/**
 * Format a date and time string or Date object in a specific timezone
 * @param dateString - ISO date string or Date object (typically in UTC from API)
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich", "America/New_York")
 * @returns Object with formatted date and time strings in the specified timezone
 */
export function formatDateTimeInTimezone(
  dateString: string | Date,
  timezone: string = "UTC",
): { date: string; time: string; dateTime: string } {
  const date = formatDateInTimezone(dateString, timezone);
  const time = formatTimeInTimezone(dateString, timezone);
  return {
    date,
    time,
    dateTime: `${date} at ${time}`,
  };
}

export function formatPrice(
  price: string | number,
  includeCurrency: boolean,
  precision: number,
  currency: string,
): string {
  if (!price) return `${includeCurrency ? currency : ""} 0`;
  return `${includeCurrency ? `${currency} ` : ""}${Number(price).toFixed(
    precision,
  )}`;
}

export function formatPriceInLocale(
  price: string | number,
  includeCurrency: boolean,
  precision: number,
  currency: string,
): string {
  const country = "de-CH";
  if (!price) return `${includeCurrency ? `${currency} ` : ""} 0`;
  return `${includeCurrency ? `${currency} ` : ""}${Number(
    price,
  ).toLocaleString(country, {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  })}`;
}

/**
 * Format a phone number with spaces for better readability
 * @param phone - Phone number string (e.g., "+41791234567", "0791234567")
 * @returns Formatted phone number with spaces (e.g., "+41 79 123 45 67", "079 123 45 67")
 */
export function formatPhone(phone: string): string {
  if (!phone) return "";

  // Remove all spaces first to normalize
  const normalized = phone.replace(/\s+/g, "");

  // Handle international format starting with +
  if (normalized.startsWith("+")) {
    const countryCode = normalized.slice(0, 3); // e.g., +41
    const rest = normalized.slice(3);

    // Group the rest of the number in pairs
    const groups: string[] = [countryCode];
    for (let i = 0; i < rest.length; i += 2) {
      groups.push(rest.slice(i, i + 2));
    }
    return groups.join(" ");
  }

  // Handle local format (no country code)
  // Group in 3-3-2-2 pattern (e.g., 079 123 45 67)
  if (normalized.length >= 9) {
    return `${normalized.slice(0, 3)} ${normalized.slice(3, 6)} ${normalized.slice(6, 8)} ${normalized.slice(8)}`.trim();
  }

  // For shorter numbers, group in pairs
  const groups: string[] = [];
  for (let i = 0; i < normalized.length; i += 2) {
    groups.push(normalized.slice(i, i + 2));
  }
  return groups.join(" ");
}

// Export time formatting utilities
export {
  formatDate as formatDateRelative,
  formatTime as formatTimeInTz,
  formatRelativeTime,
  getTodayInTimezone,
} from "./time";
